import type { ActionCardPayload, MessageSegment } from '@Types/soul';

/**
 * Action-card parser for the AI chat. The grammar is `<<<ACTION\n{json}\nACTION>>>`
 * emitted by the soul system prompt (see apps/api/src/services/ai/system-prompt.ts).
 * Used by the chat MessageContent renderer and any other admin view that needs to
 * surface action cards inline.
 */
const ACTION_REGEX = /<<<(ACTION|CHART)\n([\s\S]*?)\n\1>>>/g;

function stripIncompleteMarker(content: string): string {
  const openIdx = Math.max(content.lastIndexOf('<<<ACTION'), content.lastIndexOf('<<<CHART'));
  if (openIdx === -1) return content;
  const type = content.startsWith('<<<ACTION', openIdx) ? 'ACTION' : 'CHART';
  if (content.indexOf(`${type}>>>`, openIdx) !== -1) return content;
  return content.slice(0, openIdx);
}

export function parseMessageContent(content: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let lastIndex = 0;
  content = stripIncompleteMarker(content);

  for (const match of content.matchAll(ACTION_REGEX)) {
    const matchIndex = match.index!;
    // Text before the match
    if (matchIndex > lastIndex) {
      segments.push({ type: 'text', content: content.slice(lastIndex, matchIndex) });
    }
    // Parse JSON payload
    try {
      const type = match[1].toLowerCase() as 'action' | 'chart';
      const payload = JSON.parse(match[2]);
      segments.push({ type, payload } as MessageSegment);
    } catch {
      // Malformed JSON — render as text
      segments.push({ type: 'text', content: match[0] });
    }
    lastIndex = matchIndex + match[0].length;
  }

  // Trailing text
  if (lastIndex < content.length) {
    segments.push({ type: 'text', content: content.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', content }];
}

/** Detect incomplete markers during streaming (buffer until complete) */
export function hasIncompleteActionCard(content: string): boolean {
  const openIdx = Math.max(content.lastIndexOf('<<<ACTION'), content.lastIndexOf('<<<CHART'));
  if (openIdx === -1) return false;

  const type = content.startsWith('<<<ACTION', openIdx) ? 'ACTION' : 'CHART';
  const closeIdx = content.indexOf(`${type}>>>`, openIdx);
  return closeIdx === -1;
}

/** Returns just the action-card segments from a content string. */
export function extractActionCards(content: string): ActionCardPayload[] {
  return parseMessageContent(content)
    .filter((s): s is { type: 'action'; payload: ActionCardPayload } => s.type === 'action')
    .map((s) => s.payload);
}
